import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import AddProductModal from '../components/AddProductModal';
import EditProductModal from '../components/EditProductModal';
import CategoryModal from '../components/CategoryModal';
import SizeModal from '../components/SizeModal';
import ShippingZoneManager from '../components/ShippingZoneManager';
import QuotationModal from '../components/QuotationModal';
import PurchasesDistribution from './PurchasesDistribution';

function AdminDashboard() {
  const navigate = useNavigate();
  const { token, user, logout, isLoading } = useAuth();
  // Load last active section from localStorage, default to 'dashboard'
  const [section, setSection] = useState(() => {
    try {
      const saved = localStorage.getItem('adminActiveSection');
      return saved || 'dashboard';
    } catch {
      return 'dashboard';
    }
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSizeModalOpen, setIsSizeModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSize, setEditingSize] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceProducts, setInvoiceProducts] = useState([]);
  const [selectedInvoiceView, setSelectedInvoiceView] = useState(null);
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState('');
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
  const [showManualInvoiceModal, setShowManualInvoiceModal] = useState(false);

  // Check if user is admin - redirect to admin login if not
  // BUT allow access if password gate was unlocked (for /secret-panel route)
  // Wait for auth to load from localStorage first
  // Auto-login as admin when password gate is unlocked
  useEffect(() => {
    const adminPanelUnlocked = sessionStorage.getItem('adminPanelUnlocked') === 'true';
    const autoLoginAttempted = sessionStorage.getItem('adminAutoLoginAttempted') === 'true';
    
    // If password gate is unlocked but no token, auto-login as admin
    if (!isLoading && adminPanelUnlocked && !token && !autoLoginAttempted) {
      sessionStorage.setItem('adminAutoLoginAttempted', 'true');
      
      // Auto-login as admin@surgical.com with default password
      const loginAsAdmin = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: 'admin@surgical.com',
              password: 'admin123'
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            // Store credentials but don't reload - let auth context handle it
            console.log('✅ Auto-login successful as admin');
          } else {
            console.log('Auto-login failed:', response.status);
          }
        } catch (err) {
          console.log('Auto-login error:', err.message);
        }
      };
      
      loginAsAdmin();
    }
  }, [isLoading, token]);

  useEffect(() => {
    const adminPanelUnlocked = sessionStorage.getItem('adminPanelUnlocked') === 'true';
    if (!isLoading && !adminPanelUnlocked && (!token || !user || user.role !== 'admin')) {
      navigate('/admin-login', { replace: true });
    }
  }, [token, user, isLoading, navigate]);

  // Set page title
  useEffect(() => {
    document.title = 'Admin Page';
  }, []);

  // Save active section to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem('adminActiveSection', section);
    } catch (err) {
      console.log('Could not save section to localStorage');
    }
  }, [section]);

  const parseApiResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch {
        return {};
      }
    }

    const text = await response.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { message: text || 'Unexpected response from server' };
    }
  };

  // Fetch data from backend
  useEffect(() => {
    const adminPanelUnlocked = sessionStorage.getItem('adminPanelUnlocked') === 'true';
    // Fetch if either token exists OR admin panel is unlocked via password
    if (!isLoading && (token || adminPanelUnlocked)) {
      fetchProducts();
      fetchCategories();
      fetchSizes();
      fetchOrders();
      fetchUsers();
      fetchInvoices();
      
      // Auto-refresh orders every 5 seconds to show status updates
      const orderRefreshInterval = setInterval(() => {
        fetchOrders();
      }, 5000);
      
      return () => clearInterval(orderRefreshInterval);
    }
  }, [token, isLoading]);

  const fetchProducts = async () => {
    try {
      // Only try admin endpoint if we have a token
      if (token) {
        const response = await fetch(`${API_BASE_URL}/api/admin/products/all`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await parseApiResponse(response);
        setProducts(data.data || []);
        return;
      }
    } catch (err) {
      console.log('Could not fetch products from admin endpoint, trying public endpoint');
    }
    
    // Fallback to public endpoint if admin endpoint fails or no token
    try {
      const response = await fetch(`${API_BASE_URL}/api/products`);
      const data = await parseApiResponse(response);
      setProducts(data.data || []);
    } catch (e) {
      console.log('Could not fetch products from backend');
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const url = token ? `${API_BASE_URL}/api/admin/categories` : `${API_BASE_URL}/api/products/categories/all`;
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(url, { headers });
      const data = await parseApiResponse(response);
      setCategories(data.data || []);
    } catch (err) {
      console.log('Could not fetch categories from backend');
    }
  };

  const fetchSizes = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/admin/sizes`, { headers });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const data = await parseApiResponse(response);
      setSizes(data.data || []);
    } catch (err) {
      console.log('Could not fetch sizes');
      setSizes([]);
    }
  };

  const fetchOrders = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/orders/admin/all-orders`, { headers });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const data = await parseApiResponse(response);
      setOrders(data.data || []);
    } catch (err) {
      console.log('Could not fetch orders');
      setOrders([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, { headers });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const data = await parseApiResponse(response);
      setUsers(data.data || []);
    } catch (err) {
      console.log('Could not fetch users');
      setUsers([]);
    }
  };

  const fetchInvoices = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/admin/invoices`, { headers });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const data = await parseApiResponse(response);
      setInvoices(data.data || []);
    } catch (err) {
      console.log('Could not fetch invoices');
      setInvoices([]);
    }
  };

  const handleAddProduct = async (formData) => {
    try {
      console.log('handleAddProduct called with formData:', formData);
      const response = await fetch(`${API_BASE_URL}/api/admin/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          price: parseFloat(formData.price) || 0,
          discount: parseFloat(formData.discount) || 0,
          purchasePrice: parseFloat(formData.purchasePrice) || 0,
          barcode: formData.barcode || null,
          category: formData.category,
          image: formData.image,
          sizesData: formData.sizesData,
          sizesPricingData: formData.sizesPricingData || {}, // Add per-size pricing data for Tapes
          gst: formData.gst || 5,
          lowStockThreshold: formData.lowStockThreshold || 10,
          hasRightLeft: formData.hasRightLeft || false,
          hasNoSizeSelection: formData.hasNoSizeSelection || false,
          offers: formData.offers || [],
          hsn: formData.hsn || '',
          usesPerSizePricing: formData.usesPerSizePricing || false,
        }),
      });

      const responseData = await parseApiResponse(response);
      console.log('Response:', responseData);

      if (response.ok) {
        // Optimistic update - add product to state immediately
        const newProduct = responseData.data;
        setProducts([...products, newProduct]);
        setIsModalOpen(false);
        alert('✅ Product added successfully!');
        // Refresh in background
        await fetchProducts();
      } else {
        alert(`❌ Error: ${responseData.message || 'Failed to add product'}`);
      }
    } catch (err) {
      console.error('Error:', err);
      alert(`❌ Connection error: ${err.message}`);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      // Optimistic update - remove product from state immediately
      setProducts(products.filter(p => p.id !== productId));
      
      const response = await fetch(`${API_BASE_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const responseData = await parseApiResponse(response);

      if (response.ok) {
        alert('✅ Product deleted successfully!');
        // Refresh in background to verify
        await fetchProducts();
      } else {
        // Revert optimistic update if delete failed
        await fetchProducts();
        alert(`❌ Error: ${responseData.message || 'Failed to delete product'}`);
      }
    } catch (err) {
      // Revert optimistic update if error
      await fetchProducts();
      console.error('Delete error:', err);
      alert(`❌ Connection error: ${err.message}`);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setIsEditModalOpen(true);
  };

  const handleSaveEditProduct = async (updatedProduct) => {
    try {
      console.log('handleSaveEditProduct called with:', updatedProduct);
      const response = await fetch(`${API_BASE_URL}/api/admin/products/${updatedProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: updatedProduct.name,
          description: updatedProduct.description,
          price: parseFloat(updatedProduct.price) || 0,
          discount: parseFloat(updatedProduct.discount) || 0,
          purchasePrice: parseFloat(updatedProduct.purchasePrice) || 0,
          barcode: updatedProduct.barcode || null,
          category: updatedProduct.category,
          image: updatedProduct.image,
          sizesData: updatedProduct.sizesData,
          sizesPricingData: updatedProduct.sizesPricingData || {},
          gst: updatedProduct.gst || 5,
          lowStockThreshold: updatedProduct.lowStockThreshold || 10,
          hasRightLeft: updatedProduct.hasRightLeft || false,
          hasNoSizeSelection: updatedProduct.hasNoSizeSelection || false,
          offers: updatedProduct.offers || [],
          hsn: updatedProduct.hsn || '',
          usesPerSizePricing: updatedProduct.usesPerSizePricing || false,
        }),
      });

      const responseData = await parseApiResponse(response);

      if (response.ok) {
        setIsEditModalOpen(false);
        setEditingProduct(null);
        fetchProducts();
        alert('✅ Product updated successfully!');
      } else {
        alert(`❌ Error: ${responseData.message || 'Failed to update product'}`);
      }
    } catch (err) {
      console.error('Error:', err);
      alert(`❌ Connection error: ${err.message}`);
    }
  };

  // Invoice Functions
  const generateInvoiceNumber = () => {
    return 'INV-' + Date.now();
  };

  const createInvoice = async (selectedProducts) => {
    if (selectedProducts.length === 0) {
      alert('❌ Please select at least one product');
      return;
    }

    try {
      const invoiceNumber = generateInvoiceNumber();
      const products = selectedProducts.map(p => {
        const mrp = parseFloat(p.editableMrp ?? p.price) || 0;
        const price = parseFloat(p.editablePrice) || 0;
        const discount = parseFloat(p.editableDiscount ?? 0) || 0;
        const qty = parseInt(p.editableQty ?? 1) || 1;
        const effectivePrice = price > 0 ? price : Math.round(mrp - (mrp * discount / 100));
        const effectiveDiscount = effectivePrice > 0 && mrp > 0 ? ((mrp - effectivePrice) / mrp) * 100 : discount;

        return {
          name: p.name,
          hsn: p.hsn || '',
          sizeName: p.sizeName || '',
          mrp,
          price: effectivePrice,
          discount: effectiveDiscount,
          gst: parseFloat(p.editableGst ?? 5) || 5,
          qty,
          sellingPrice: effectivePrice * qty,
        };
      });

      const totalAmount = products.reduce((sum, p) => sum + (p.sellingPrice || 0), 0);

      const response = await fetch(`${API_BASE_URL}/api/admin/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoiceNumber,
          totalAmount,
          productDetails: {
            date: new Date().toLocaleDateString('en-IN'),
            products,
          },
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Fetch updated invoices list
        fetchInvoices();
        setSelectedInvoiceView({
          ...data.data,
          productDetails: typeof data.data.productDetails === 'string' ? JSON.parse(data.data.productDetails) : data.data.productDetails,
        });
        setShowInvoiceModal(false);
        setInvoiceProducts([]);
        setInvoiceSearchQuery('');
        setExpandedProductId(null);
        alert('✅ Invoice created successfully!');
      } else {
        alert(`❌ Error: ${data.message || 'Failed to create invoice'}`);
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      alert(`❌ Connection error: ${err.message}`);
    }
  };

  const createManualInvoice = async (manualItems) => {
    if (!manualItems || manualItems.length === 0) {
      alert('❌ Please add at least one item');
      return;
    }

    try {
      const invoiceNumber = generateInvoiceNumber();
      const products = manualItems.map(item => {
        const mrp = parseFloat(item.mrp) || 0;
        const discount = parseFloat(item.discount) || 0;
        const price = parseFloat(item.price) || 0;
        const qty = parseInt(item.qty) || 1;
        const effectivePrice = price > 0 ? price : Math.round(mrp - (mrp * discount / 100));
        const effectiveDiscount = price > 0 && mrp > 0 ? ((mrp - price) / mrp) * 100 : discount;

        return {
          name: item.name,
          hsn: item.hsn || '',
          mrp,
          price: effectivePrice,
          discount: effectiveDiscount,
          gst: parseFloat(item.gst) || 5,
          qty,
          sellingPrice: effectivePrice * qty,
        };
      });

      const totalAmount = products.reduce((sum, p) => sum + (p.sellingPrice || 0), 0);

      const response = await fetch(`${API_BASE_URL}/api/admin/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoiceNumber,
          totalAmount,
          productDetails: {
            date: new Date().toLocaleDateString('en-IN'),
            products,
            type: 'manual',
          },
        }),
      });

      const data = await response.json();

      if (response.ok) {
        fetchInvoices();
        setSelectedInvoiceView({
          ...data.data,
          productDetails: typeof data.data.productDetails === 'string' ? JSON.parse(data.data.productDetails) : data.data.productDetails,
        });
        setShowManualInvoiceModal(false);
        alert('✅ Manual invoice created successfully!');
      } else {
        alert(`❌ Error: ${data.message || 'Failed to create manual invoice'}`);
      }
    } catch (err) {
      console.error('Error creating manual invoice:', err);
      alert(`❌ Connection error: ${err.message}`);
    }
  };

  const handleAddCategory = async (categoryData) => {
    try {
      const method = categoryData.id ? 'PUT' : 'POST';
      const url = categoryData.id 
        ? `${API_BASE_URL}/api/admin/categories/${categoryData.id}`
        : `${API_BASE_URL}/api/admin/categories`;

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: categoryData.name,
          description: categoryData.description,
          sizesData: categoryData.sizesData || [],
          usesPerSizePricing: categoryData.usesPerSizePricing || false,
        }),
      });

      const responseData = await response.json();

      if (response.ok) {
        setIsCategoryModalOpen(false);
        setEditingCategory(null);
        fetchCategories();
        alert(categoryData.id ? '✅ Category updated successfully!' : '✅ Category added successfully!');
      } else {
        alert(`❌ Error: ${responseData.message || 'Failed to save category'}`);
      }
    } catch (err) {
      console.error('Error:', err);
      alert(`❌ Connection error: ${err.message}`);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchCategories();
        alert('✅ Category deleted successfully!');
      } else {
        alert('❌ Error deleting category');
      }
    } catch (err) {
      alert('Connection error');
    }
  };

  const handleOpenCategoryModal = (category = null) => {
    setEditingCategory(category);
    setIsCategoryModalOpen(true);
  };

  const handleAddSize = async (sizeData) => {
    try {
      const method = sizeData.id ? 'PUT' : 'POST';
      const url = sizeData.id 
        ? `${API_BASE_URL}/api/admin/sizes/${sizeData.id}`
        : `${API_BASE_URL}/api/admin/sizes`;

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: sizeData.name,
          description: sizeData.description,
          type: sizeData.type,
        }),
      });

      const responseData = await response.json();

      if (response.ok) {
        setIsSizeModalOpen(false);
        setEditingSize(null);
        fetchSizes();
        alert(sizeData.id ? '✅ Size updated successfully!' : '✅ Size added successfully!');
      } else {
        alert(`❌ Error: ${responseData.message || 'Failed to save size'}`);
      }
    } catch (err) {
      console.error('Error:', err);
      alert(`❌ Connection error: ${err.message}`);
    }
  };

  const handleDeleteSize = async (sizeId) => {
    if (!window.confirm('Are you sure you want to delete this size?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/sizes/${sizeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchSizes();
        alert('✅ Size deleted successfully!');
      } else {
        alert('❌ Error deleting size');
      }
    } catch (err) {
      alert('Connection error');
    }
  };

  const handleUpdateSizeQuantity = async (productId, sizeId, newQuantity) => {
    if (newQuantity < 0) return;

    // Update state immediately for instant UI update
    const updatedProducts = products.map(product => {
      if (product.id === productId) {
        return {
          ...product,
          sizes: product.sizes.map(size => 
            size.sizeId === sizeId ? { ...size, quantity: newQuantity } : size
          )
        };
      }
      return product;
    });
    setProducts(updatedProducts);

    // Make API call in background
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/products/${productId}/size/${sizeId}/update-quantity`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity: newQuantity }),
      });

      if (!response.ok) {
        alert('❌ Error updating quantity');
        fetchProducts(); // Refetch if error
      }
    } catch (err) {
      alert('Connection error');
      fetchProducts(); // Refetch if error
    }
  };

  const handleOpenSizeModal = (size = null) => {
    setEditingSize(size);
    setIsSizeModalOpen(true);
  };



  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      {/* Admin Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%)`,
          color: 'white',
          paddingTop: spacing['2xl'],
          paddingBottom: spacing['2xl'],
          boxShadow: shadows.lg,
        }}
      >
        <div className="container">
          <h1
            style={{
              fontSize: '2.5rem',
              fontWeight: 900,
              marginBottom: spacing.md,
              letterSpacing: '-1px',
            }}
          >
            📊 Admin Dashboard
          </h1>
          <p
            style={{
              fontSize: '1.05rem',
              color: colors.primary[100],
              fontWeight: 500,
              margin: 0,
            }}
          >
            Manage products, orders, categories, sizes, sales &amp; distribution, quotations and purchases
          </p>
        </div>
      </motion.div>

      <div className="container" style={{ paddingTop: spacing.xl, paddingBottom: spacing.xl }}>
        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: spacing.md,
            marginBottom: spacing.xl,
          }}
        >
          {[
            { key: 'dashboard', label: '📊 Dashboard', icon: '📊' },
            { key: 'products', label: '📦 Products', icon: '📦' },
            { key: 'lowStock', label: '⚠️ Low Stock', icon: '⚠️' },
            { key: 'invoices', label: '📄 Sales', icon: '📄' },
            { key: 'quotations', label: '📋 Quotations', icon: '📋' },
            { key: 'categories', label: '📂 Categories', icon: '📂' },
            { key: 'sizes', label: '📏 Sizes', icon: '📏' },
            { key: 'shipping', label: '🚚 Shipping Zones', icon: '🚚' },
            { key: 'orders', label: '🛒 Orders', icon: '🛒' },
            { key: 'purchases', label: '💰 Purchases', icon: '💰' },
            { key: 'users', label: '👥 Users', icon: '👥' },
          ].map((tab) => (
            <motion.button
              key={tab.key}
              onClick={() => setSection(tab.key)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: spacing.lg,
                borderRadius: '0.75rem',
                fontWeight: 700,
                fontSize: '0.95rem',
                border: 'none',
                cursor: 'pointer',
                backgroundColor:
                  section === tab.key
                    ? colors.primary[500]
                    : colors.background,
                color: section === tab.key ? 'white' : colors.text.primary,
                boxShadow:
                  section === tab.key ? shadows.lg : shadows.sm,
                transition: 'all 0.3s ease',
              }}
            >
              {tab.label}
            </motion.button>
          ))}
        </motion.div>

        {/* Dashboard Overview */}
        {section === 'dashboard' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: spacing.lg,
              marginBottom: spacing.xl,
            }}
          >
            {[
              { icon: '📦', label: 'Total Products', value: products.length, color: colors.primary[500] },
              { icon: '🛒', label: 'Total Orders', value: orders.length, color: colors.success },
              { icon: '👥', label: 'Total Users', value: users.length, color: colors.secondary[500] },
              { icon: '💰', label: 'Total Revenue', value: `₹${orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0)}`, color: colors.accent[500] },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                whileHover={{ scale: 1.05 }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: shadows.md,
                  padding: spacing.lg,
                  borderTop: `4px solid ${stat.color}`,
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: spacing.md }}>
                  {stat.icon}
                </div>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: colors.text.secondary,
                    fontWeight: 600,
                    marginBottom: spacing.sm,
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: '1.75rem',
                    fontWeight: 900,
                    color: stat.color,
                  }}
                >
                  {stat.value}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Products Section */}
        {section === 'products' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: shadows.md,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: spacing.lg, borderBottom: `2px solid ${colors.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
                <h2
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    color: colors.text.primary,
                    margin: 0,
                  }}
                >
                  📦 Products ({products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())).length})
                </h2>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsModalOpen(true)}
                  style={{
                    backgroundColor: colors.primary[500],
                    color: 'white',
                    padding: `${spacing.sm} ${spacing.lg}`,
                    borderRadius: '0.5rem',
                    border: 'none',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  + Add Product
                </motion.button>
              </div>

              {/* Search Bar */}
              <input
                type="text"
                placeholder="🔍 Search products by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: `${spacing.md} ${spacing.lg}`,
                  border: `2px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary[600];
                  e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead
                  style={{
                    backgroundColor: colors.primary[600],
                    borderBottom: `2px solid ${colors.border}`,
                  }}
                >
                  <tr>
                    {['Product Name', 'HSN', 'Category', 'Purchase Price', 'Price (MRP)', 'Discount', 'Selling Price', 'R/L', 'Actions'].map((header) => (
                      <th
                        key={header}
                        style={{
                          padding: spacing.lg,
                          textAlign: 'left',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          color: 'white',
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products
                    .filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((product) => {
                    const sellingPrice = (product.price - (product.price * product.discount / 100)).toFixed(2);
                    const isExpanded = expandedProducts[product.id];
                    return (
                      <React.Fragment key={product.id}>
                        <tr
                          style={{
                            borderBottom: `1px solid ${colors.border}`,
                            transition: 'background-color 0.2s ease',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colors.primary[50];
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <td style={{ padding: spacing.md, color: colors.text.primary, fontWeight: 600 }}>
                            <motion.button
                              onClick={() => setExpandedProducts({ ...expandedProducts, [product.id]: !isExpanded })}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                marginRight: spacing.sm,
                              }}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </motion.button>
                            {product.name}
                          </td>
                          <td style={{ padding: spacing.md, color: colors.text.secondary, fontSize: '0.85rem' }}>
                            {product.hsn || '—'}
                          </td>
                          <td style={{ padding: spacing.md, color: colors.text.secondary }}>
                            {product.category}
                          </td>
                          <td style={{ padding: spacing.md, color: colors.secondary[600], fontWeight: 700 }}>
                            {product.category?.toLowerCase() === 'tapes' ? '📍' : `₹${Math.round(product.purchasePrice || 0)}`}
                          </td>
                          <td style={{ padding: spacing.md, color: colors.primary[600], fontWeight: 700 }}>
                            {product.category?.toLowerCase() === 'tapes' ? '📍' : `₹${Math.round(product.price)}`}
                          </td>
                          <td style={{ padding: spacing.md, color: colors.error, fontWeight: 700 }}>
                            {product.category?.toLowerCase() === 'tapes' ? '📍' : `${product.discount}%`}
                          </td>
                          <td style={{ padding: spacing.md, color: colors.success, fontWeight: 700, fontSize: '0.875rem' }}>
                            {product.category?.toLowerCase() === 'tapes' ? '👇 Click ▼' : `₹${Math.round(parseFloat(sellingPrice))}`}
                          </td>
                          <td style={{ padding: spacing.md, textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: `${spacing.xs} ${spacing.sm}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              backgroundColor: product.hasRightLeft ? colors.success + '20' : colors.neutral[200],
                              color: product.hasRightLeft ? colors.success : colors.text.secondary,
                            }}>
                              {product.hasRightLeft ? '✅ Yes' : '❌ No'}
                            </span>
                          </td>
                          <td style={{ padding: spacing.md }}>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              onClick={() => handleEditProduct(product)}
                              style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: colors.primary[600],
                                fontWeight: 700,
                                cursor: 'pointer',
                                marginRight: spacing.lg,
                                fontSize: '0.875rem',
                              }}
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              onClick={() => handleDeleteProduct(product.id)}
                              style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: colors.error,
                                fontWeight: 700,
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                              }}
                            >
                              Delete
                            </motion.button>
                          </td>
                        </tr>

                        {/* Expanded Row - Size Inventory */}
                        {isExpanded && (
                          <tr style={{ backgroundColor: colors.primary[25], borderBottom: `2px solid ${colors.border}` }}>
                            <td colSpan="9" style={{ padding: spacing.lg }}>
                              {(() => {
                                // Helper to check if category uses per-size pricing
                                const categoryData = categories.find(c => c.name?.toLowerCase() === product.category?.toLowerCase());
                                const isTapesCategory = categoryData?.usesPerSizePricing === true || product.category?.toLowerCase() === 'tapes';
                                
                                if (isTapesCategory) {
                                  // For Tapes: Show detailed pricing table for each size
                                  return (
                                    <div style={{ overflowX: 'auto' }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                          <tr style={{ backgroundColor: colors.secondary[100], borderBottom: `2px solid ${colors.border}` }}>
                                            <th style={{ padding: spacing.md, textAlign: 'left', fontWeight: 700, color: colors.text.primary, fontSize: '0.85rem' }}>Inch Size</th>
                                            <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, color: colors.secondary[600], fontSize: '0.85rem' }}>Purchase Price</th>
                                            <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, color: colors.primary[600], fontSize: '0.85rem' }}>MRP</th>
                                            <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, color: colors.error, fontSize: '0.85rem' }}>Discount</th>
                                            <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, color: colors.success, fontSize: '0.85rem' }}>Selling Price</th>
                                            <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, color: colors.text.primary, fontSize: '0.85rem' }}>Stock</th>
                                            <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, color: colors.text.primary, fontSize: '0.85rem' }}>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {product.sizes && product.sizes.length > 0 ? (
                                            product.sizes.map((size, idx) => {
                                              const displayPrice = parseFloat(size.price) || 0;
                                              const displayCost = parseFloat(size.purchasePrice) || 0;
                                              const displayDiscount = parseFloat(size.discount) || 0;
                                              
                                              // Ensure discount is treated as percentage (0-100), not decimal
                                              const discountPercent = displayDiscount > 1 ? displayDiscount / 100 : displayDiscount;
                                              
                                              // For admin view: show selling price from MRP
                                              const sellingPrice = Math.round(displayPrice - (displayPrice * discountPercent));
                                              
                                              return (
                                                <tr key={size.sizeId} style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: idx % 2 === 0 ? 'white' : colors.primary[25] }}>
                                                  <td style={{ padding: spacing.md, fontWeight: 600, color: colors.text.primary }}>
                                                    {size.name}"
                                                  </td>
                                                  <td style={{ padding: spacing.md, textAlign: 'center', color: colors.secondary[600], fontWeight: 600 }}>
                                                    ₹{Math.round(displayCost)}
                                                  </td>
                                                  <td style={{ padding: spacing.md, textAlign: 'center', color: colors.primary[600], fontWeight: 600 }}>
                                                    ₹{Math.round(displayPrice)}
                                                  </td>
                                                  <td style={{ padding: spacing.md, textAlign: 'center', color: colors.error, fontWeight: 600 }}>
                                                    {displayDiscount > 1 ? Math.round(displayDiscount) : Math.round(displayDiscount * 100)}%
                                                  </td>
                                                  <td style={{ padding: spacing.md, textAlign: 'center', color: colors.success, fontWeight: 700 }}>
                                                    ₹{sellingPrice}
                                                  </td>
                                                  <td style={{ padding: spacing.md, textAlign: 'center', color: size.quantity <= 0 ? colors.error : colors.success, fontWeight: 700 }}>
                                                    {size.quantity || 0} {size.quantity <= 0 ? '❌' : '✅'}
                                                  </td>
                                                  <td style={{ padding: spacing.md, textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'center' }}>
                                                      <motion.button
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        onClick={() => handleUpdateSizeQuantity(product.id, size.sizeId, (size.quantity || 0) - 1)}
                                                        style={{
                                                          backgroundColor: colors.error,
                                                          color: 'white',
                                                          border: 'none',
                                                          borderRadius: '0.25rem',
                                                          padding: `${spacing.xs} ${spacing.sm}`,
                                                          fontWeight: 700,
                                                          fontSize: '0.75rem',
                                                          cursor: 'pointer',
                                                          minWidth: '1.5rem',
                                                        }}
                                                      >
                                                        −
                                                      </motion.button>
                                                      <motion.button
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        onClick={() => handleUpdateSizeQuantity(product.id, size.sizeId, (size.quantity || 0) + 1)}
                                                        style={{
                                                          backgroundColor: colors.success,
                                                          color: 'white',
                                                          border: 'none',
                                                          borderRadius: '0.25rem',
                                                          padding: `${spacing.xs} ${spacing.sm}`,
                                                          fontWeight: 700,
                                                          fontSize: '0.75rem',
                                                          cursor: 'pointer',
                                                          minWidth: '1.5rem',
                                                        }}
                                                      >
                                                        +
                                                      </motion.button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })
                                          ) : (
                                            <tr>
                                              <td colSpan="7" style={{ padding: spacing.lg, textAlign: 'center', color: colors.text.secondary }}>
                                                No sizes configured for this product
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                } else {
                                  // Non-Tapes: Show simple size table
                                  return (
                                    <div style={{ overflowX: 'auto' }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                          <tr style={{ backgroundColor: colors.secondary[100], borderBottom: `2px solid ${colors.border}` }}>
                                            <th style={{ padding: spacing.md, textAlign: 'left', fontWeight: 700, color: colors.text.primary, fontSize: '0.85rem' }}>Size</th>
                                            <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, color: colors.success, fontSize: '0.85rem' }}>Stock</th>
                                            <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, color: colors.text.primary, fontSize: '0.85rem' }}>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {product.sizes && product.sizes.length > 0 ? (
                                            product.sizes.map((size, idx) => (
                                              <tr key={size.sizeId} style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: idx % 2 === 0 ? 'white' : colors.primary[25] }}>
                                                <td style={{ padding: spacing.md, fontWeight: 600, color: colors.text.primary }}>
                                                  {size.name}
                                                </td>
                                                <td style={{ padding: spacing.md, textAlign: 'center', color: size.quantity <= 0 ? colors.error : colors.success, fontWeight: 700 }}>
                                                  {size.quantity || 0} {size.quantity <= 0 ? '❌' : '✅'}
                                                </td>
                                                <td style={{ padding: spacing.md, textAlign: 'center' }}>
                                                  <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'center' }}>
                                                    <motion.button
                                                      whileHover={{ scale: 1.1 }}
                                                      whileTap={{ scale: 0.95 }}
                                                      onClick={() => handleUpdateSizeQuantity(product.id, size.sizeId, (size.quantity || 0) - 1)}
                                                      style={{
                                                        backgroundColor: colors.error,
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '0.25rem',
                                                        padding: `${spacing.xs} ${spacing.sm}`,
                                                        fontWeight: 700,
                                                        fontSize: '0.75rem',
                                                        cursor: 'pointer',
                                                        minWidth: '1.5rem',
                                                      }}
                                                    >
                                                      −
                                                    </motion.button>
                                                    <motion.button
                                                      whileHover={{ scale: 1.1 }}
                                                      whileTap={{ scale: 0.95 }}
                                                      onClick={() => handleUpdateSizeQuantity(product.id, size.sizeId, (size.quantity || 0) + 1)}
                                                      style={{
                                                        backgroundColor: colors.success,
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '0.25rem',
                                                        padding: `${spacing.xs} ${spacing.sm}`,
                                                        fontWeight: 700,
                                                        fontSize: '0.75rem',
                                                        cursor: 'pointer',
                                                        minWidth: '1.5rem',
                                                      }}
                                                    >
                                                      +
                                                    </motion.button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr>
                                              <td colSpan="3" style={{ padding: spacing.lg, textAlign: 'center', color: colors.text.secondary }}>
                                                No sizes configured for this product
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                }
                              })()}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Low Stock Section */}
        {section === 'lowStock' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: shadows.md,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: spacing.lg, borderBottom: `2px solid ${colors.border}` }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: colors.text.primary, margin: 0 }}>
                ⚠️ Low Stock Products
              </h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: colors.success, borderBottom: `2px solid ${colors.border}` }}>
                  <tr>
                    {['Product Name', 'Category', 'Size', 'Stock', 'Low Stock Threshold', 'Status'].map((header) => (
                      <th key={header} style={{ padding: spacing.lg, textAlign: 'left', fontSize: '0.875rem', fontWeight: 700, color: 'white' }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.flatMap(product => 
                    (product.sizes || [])
                      .filter(size => size.quantity <= (product.lowStockThreshold || 10))
                      .map((size, idx) => (
                        <tr key={`${product.id}-${size.sizeId}`} style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: idx % 2 === 0 ? 'white' : colors.error + '08' }}>
                          <td style={{ padding: spacing.md, fontWeight: 600, color: colors.text.primary }}>{product.name}</td>
                          <td style={{ padding: spacing.md, color: colors.text.secondary }}>{product.category}</td>
                          <td style={{ padding: spacing.md, fontWeight: 600 }}>{size.name}</td>
                          <td style={{ padding: spacing.md, fontWeight: 700, color: colors.error }}>{size.quantity}</td>
                          <td style={{ padding: spacing.md, color: colors.text.secondary }}>{product.lowStockThreshold || 10}</td>
                          <td style={{ padding: spacing.md }}>
                            <span style={{
                              display: 'inline-block',
                              padding: `${spacing.xs} ${spacing.md}`,
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              backgroundColor: colors.error + '20',
                              color: colors.error,
                            }}>
                              ⚠️ Low Stock
                            </span>
                          </td>
                        </tr>
                      ))
                  )}
                  {products.flatMap(p => (p.sizes || []).filter(s => s.quantity <= (p.lowStockThreshold || 10))).length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ padding: spacing.xl, textAlign: 'center', color: colors.text.secondary }}>
                        ✅ All products have sufficient stock
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Orders Section */}
        {section === 'orders' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: shadows.md,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: spacing.lg, borderBottom: `2px solid ${colors.border}` }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: colors.text.primary, margin: 0 }}>
                🛒 Orders ({orders.length})
              </h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: colors.primary[600], borderBottom: `2px solid ${colors.border}` }}>
                  <tr>
                    {['Order ID', 'Customer', 'Phone', 'Address', 'Items', 'Total', 'Status', 'Payment', 'Date'].map((header) => (
                      <th key={header} style={{ padding: spacing.lg, textAlign: 'left', fontSize: '0.875rem', fontWeight: 700, color: 'white' }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const items = order.items || [];
                    return (
                      <tr key={order.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={{ padding: spacing.md, fontWeight: 700, color: colors.primary[600] }}>
                          #{order.id}
                        </td>
                        <td style={{ padding: spacing.md }}>
                          <p style={{ fontWeight: 600, color: colors.text.primary, margin: 0 }}>{order.customer || 'N/A'}</p>
                          <p style={{ fontSize: '0.8rem', color: colors.text.secondary, margin: 0 }}>{order.customerEmail}</p>
                        </td>
                        <td style={{ padding: spacing.md, color: colors.text.secondary, fontSize: '0.85rem', fontWeight: 600 }}>
                          📱 {order.customerPhone || 'N/A'}
                        </td>
                        <td style={{ padding: spacing.md, color: colors.text.secondary, fontSize: '0.8rem', maxWidth: '200px' }}>
                          {(() => {
                            const parts = [
                              order.building,
                              order.addressLine1,
                              order.addressLine2,
                              order.landmark
                            ].filter(Boolean);
                            return parts.length > 0 ? parts.join(', ') : 'N/A';
                          })()}
                        </td>
                        <td style={{ padding: spacing.md }}>
                          <p style={{ fontSize: '0.85rem', color: colors.text.primary, margin: 0 }}>{items.length} item(s)</p>
                          <div style={{ fontSize: '0.75rem', color: colors.text.secondary, marginTop: spacing.xs }}>
                            {items.slice(0, 2).map((item, i) => (
                              <p key={i} style={{ margin: 0 }}>
                                {item.name} × {item.quantity}
                                {item.side && (
                                  <span style={{ fontWeight: 700, color: colors.text.primary }}>
                                    {' '}({item.side === 'right' ? 'Right' : 'Left'} side)
                                  </span>
                                )}
                              </p>
                            ))}
                            {items.length > 2 && <p style={{ margin: 0 }}>+{items.length - 2} more</p>}
                          </div>
                        </td>
                        <td style={{ padding: spacing.md, fontWeight: 700, color: colors.primary[600] }}>
                          ₹{Math.round(order.amount || order.totalAmount || 0)}
                        </td>
                        <td style={{ padding: spacing.md }}>
                          <span style={{
                            display: 'inline-block',
                            padding: `${spacing.xs} ${spacing.md}`,
                            borderRadius: '9999px',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            backgroundColor: order.status === 'confirmed' || order.status === 'completed'
                              ? colors.success + '20'
                              : order.status === 'payment_pending' || order.status === 'cod_pending'
                                ? colors.warning + '20'
                                : colors.error + '20',
                            color: order.status === 'confirmed' || order.status === 'completed'
                              ? colors.success
                              : order.status === 'payment_pending' || order.status === 'cod_pending'
                                ? colors.warning
                                : colors.error,
                          }}>
                            {order.status}
                          </span>
                        </td>
                        <td style={{ padding: spacing.md, color: colors.text.secondary, fontSize: '0.9rem' }}>
                          {order.paymentMethod || 'N/A'}
                        </td>
                        <td style={{ padding: spacing.md, color: colors.text.secondary, fontSize: '0.85rem' }}>
                          {new Date(order.date || order.createdAt).toLocaleDateString('en-IN')}
                        </td>
                      </tr>
                    );
                  })}
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan="9" style={{ padding: spacing.xl, textAlign: 'center', color: colors.text.secondary }}>
                        No orders yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Users Section */}
        {section === 'users' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: shadows.md,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: spacing.lg, borderBottom: `2px solid ${colors.border}` }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: colors.text.primary, margin: 0 }}>
                👥 Users ({users.length})
              </h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: colors.primary[600], borderBottom: `2px solid ${colors.border}` }}>
                  <tr>
                    {['Name', 'Phone', 'Email', 'Role', 'Actions'].map((header) => (
                      <th
                        key={header}
                        style={{
                          padding: spacing.lg,
                          textAlign: 'left',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          color: 'white',
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        transition: 'background-color 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary[50];
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td style={{ padding: spacing.md, color: colors.text.primary, fontWeight: 600 }}>
                        {user.name}
                      </td>
                      <td style={{ padding: spacing.md, color: colors.text.secondary }}>
                        📱 {user.phone || 'N/A'}
                      </td>
                      <td style={{ padding: spacing.md, color: colors.text.secondary }}>
                        {user.email}
                      </td>
                      <td style={{ padding: spacing.md }}>
                        <span style={{
                          display: 'inline-block',
                          padding: `${spacing.xs} ${spacing.md}`,
                          borderRadius: '9999px',
                          fontSize: '0.875rem',
                          fontWeight: 700,
                          backgroundColor: user.role === 'admin' ? colors.primary[100] : colors.secondary[100],
                          color: user.role === 'admin' ? colors.primary[800] : colors.secondary[800],
                        }}>
                          {user.role}
                        </span>
                      </td>
                      <td style={{ padding: spacing.md }}>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: colors.primary[600],
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                          }}
                        >
                          View
                        </motion.button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Categories Section */}
        {section === 'categories' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: shadows.md,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: spacing.lg, borderBottom: `2px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: colors.text.primary, margin: 0 }}>
                📂 Categories
              </h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleOpenCategoryModal()}
                style={{
                  backgroundColor: colors.primary[500],
                  color: 'white',
                  padding: `${spacing.sm} ${spacing.lg}`,
                  borderRadius: '0.5rem',
                  border: 'none',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                + Add Category
              </motion.button>
            </div>
            <div style={{ padding: spacing.lg }}>
              <div style={{ display: 'grid', gap: spacing.md }}>
                {categories.map((cat) => (
                  <div key={cat.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: spacing.lg,
                    backgroundColor: colors.primary[50],
                    borderRadius: '0.5rem',
                    border: `1px solid ${colors.border}`,
                  }}>
                    <div>
                      <p style={{ fontWeight: 700, color: colors.text.primary, margin: 0 }}>{cat.name}</p>
                      <p style={{ fontSize: '0.85rem', color: colors.text.secondary, margin: 0 }}>
                        {cat.sizes?.length || 0} sizes | Per-size pricing: {cat.usesPerSizePricing ? '✅ Yes' : '❌ No'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: spacing.md }}>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        onClick={() => handleOpenCategoryModal(cat)}
                        style={{
                          backgroundColor: colors.primary[600],
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.3rem',
                          padding: `${spacing.xs} ${spacing.md}`,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                        }}
                      >
                        Edit
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        onClick={() => handleDeleteCategory(cat.id)}
                        style={{
                          backgroundColor: colors.error,
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.3rem',
                          padding: `${spacing.xs} ${spacing.md}`,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                        }}
                      >
                        Delete
                      </motion.button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Sizes Section */}
        {section === 'sizes' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: shadows.md,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: spacing.lg, borderBottom: `2px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: colors.text.primary, margin: 0 }}>
                📏 Sizes
              </h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleOpenSizeModal()}
                style={{
                  backgroundColor: colors.primary[500],
                  color: 'white',
                  padding: `${spacing.sm} ${spacing.lg}`,
                  borderRadius: '0.5rem',
                  border: 'none',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                + Add Size
              </motion.button>
            </div>
            <div style={{ padding: spacing.lg }}>
              <div style={{ display: 'grid', gap: spacing.md, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                {sizes.map((size) => (
                  <div key={size.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: spacing.md,
                    backgroundColor: colors.primary[50],
                    borderRadius: '0.5rem',
                    border: `1px solid ${colors.border}`,
                  }}>
                    <div>
                      <p style={{ fontWeight: 700, color: colors.text.primary, margin: 0 }}>{size.name}</p>
                      <p style={{ fontSize: '0.8rem', color: colors.text.secondary, margin: 0 }}>{size.description || ''}</p>
                      <span style={{
                        display: 'inline-block',
                        marginTop: '0.25rem',
                        padding: `${spacing.xs} ${spacing.sm}`,
                        borderRadius: '9999px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        backgroundColor: size.type === 'inch' ? colors.warning + '20' : size.type === 'slipper' ? colors.secondary[100] : colors.primary[100],
                        color: size.type === 'inch' ? colors.warning : size.type === 'slipper' ? colors.secondary[700] : colors.primary[700],
                      }}>
                        {size.type || 'standard'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: spacing.sm }}>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        onClick={() => handleOpenSizeModal(size)}
                        style={{
                          backgroundColor: colors.primary[600],
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                        }}
                      >
                        Edit
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        onClick={() => handleDeleteSize(size.id)}
                        style={{
                          backgroundColor: colors.error,
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                        }}
                      >
                        Del
                      </motion.button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Shipping Zones Section */}
        {section === 'shipping' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: shadows.md,
              overflow: 'hidden',
            }}
          >
            <ShippingZoneManager />
          </motion.div>
        )}

        {/* Sales Section (customer invoices + distribution to stores) */}
        {section === 'invoices' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div style={{ marginBottom: spacing.xl }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: spacing.lg,
                  gap: spacing.md,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <h2
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      color: colors.text.primary,
                      margin: 0,
                    }}
                  >
                    📄 Sales
                  </h2>
                  <p style={{ margin: spacing.xs + ' 0 0', color: colors.text.secondary, fontSize: '0.92rem' }}>
                    Customer invoices and sales to other stores (distribution)
                  </p>
                </div>
                <div style={{ display: 'flex', gap: spacing.md }}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowInvoiceModal(true);
                      setInvoiceProducts([]);
                      setInvoiceSearchQuery('');
                    }}
                    style={{
                      backgroundColor: colors.primary[500],
                      color: 'white',
                      padding: `${spacing.sm} ${spacing.lg}`,
                      borderRadius: '0.5rem',
                      border: 'none',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}
                  >
                    + Create Invoice
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowManualInvoiceModal(true)}
                    style={{
                      backgroundColor: colors.secondary[500],
                      color: 'white',
                      padding: `${spacing.sm} ${spacing.lg}`,
                      borderRadius: '0.5rem',
                      border: 'none',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}
                  >
                    + Manual Invoice
                  </motion.button>
                </div>
              </div>

              <h3
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 800,
                  color: colors.text.primary,
                  margin: 0,
                  marginBottom: spacing.md,
                }}
              >
                🧾 Customer Invoices ({invoices.length})
              </h3>

              {/* Invoice List */}
              {invoices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.text.secondary }}>
                  <p style={{ fontSize: '1.1rem' }}>No invoices yet. Create one to get started!</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: spacing.lg, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                  {invoices.map((invoice) => {
                    const productDetails = typeof invoice.productDetails === 'string' ? JSON.parse(invoice.productDetails) : invoice.productDetails;
                    const products = productDetails?.products || [];
                    return (
                    <motion.div
                      key={invoice.id}
                      whileHover={{ y: -4 }}
                      onClick={() => setSelectedInvoiceView({
                        ...invoice,
                        productDetails: productDetails,
                      })}
                      style={{
                        border: `2px solid ${colors.primary[200]}`,
                        borderRadius: '0.75rem',
                        padding: spacing.lg,
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = shadows.lg;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <p style={{ fontSize: '1rem', fontWeight: 900, color: colors.primary[600], margin: 0, marginBottom: spacing.sm }}>
                        {invoice.invoiceNumber}
                      </p>
                      <p style={{ fontSize: '0.875rem', color: colors.text.secondary, margin: 0, marginBottom: spacing.xs }}>
                        📅 {new Date(invoice.createdAt).toLocaleDateString('en-IN')}
                      </p>
                      <p style={{ fontSize: '0.875rem', color: colors.text.secondary, margin: 0 }}>
                        🛍️ {products.length} product(s)
                      </p>
                      <p style={{ fontSize: '1rem', fontWeight: 700, color: colors.primary[600], marginTop: spacing.md, margin: 0 }}>
                        Total: ₹{parseFloat(invoice.totalAmount).toFixed(2)}
                      </p>
                    </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Distribution / store sales - the finance module that used to sit
                inside the Purchases tab, now part of Sales. */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '0.75rem',
                boxShadow: shadows.md,
                padding: spacing.lg,
                marginTop: spacing.xl,
              }}
            >
              <PurchasesDistribution moduleKey='distributions' />
            </motion.div>
          </motion.div>
        )}

        {/* Quotations Section */}
        {section === 'quotations' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div style={{ marginBottom: spacing.xl }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
                <h2
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    color: colors.text.primary,
                    margin: 0,
                  }}
                >
                  📋 Quotations
                </h2>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsQuotationModalOpen(true)}
                  style={{
                    backgroundColor: colors.primary[500],
                    color: 'white',
                    padding: `${spacing.sm} ${spacing.lg}`,
                    borderRadius: '0.5rem',
                    border: 'none',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  + Create Quotation
                </motion.button>
              </div>
              <p style={{ color: colors.text.secondary, fontSize: '0.95rem' }}>
                Create professional quotations with manually entered items, MRP, quantities, and auto-calculated totals.
              </p>
            </div>
          </motion.div>
        )}

        {/* Purchases Section */}
        {section === 'purchases' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: shadows.md,
              padding: spacing.lg,
            }}
          >
            <PurchasesDistribution moduleKey='purchases' />
          </motion.div>
        )}
      </div>

      {/* Add Product Modal */}
      <AddProductModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddProduct}
        categories={categories}
        sizes={sizes}
      />

      {/* Edit Product Modal */}
      <EditProductModal 
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingProduct(null);
        }}
        onEdit={handleSaveEditProduct}
        product={editingProduct}
        categories={categories}
        sizes={sizes}
      />

      {/* Category Modal */}
      <CategoryModal 
        isOpen={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
          setEditingCategory(null);
        }}
        onAdd={handleAddCategory}
        editingCategory={editingCategory}
        sizes={sizes}
      />

      {/* Size Modal */}
      <SizeModal 
        isOpen={isSizeModalOpen}
        onClose={() => {
          setIsSizeModalOpen(false);
          setEditingSize(null);
        }}
        onAdd={handleAddSize}
        editingSize={editingSize}
      />

      {/* Create Invoice Modal with editable fields */}
      {showInvoiceModal && (
        <CreateInvoiceModal
          products={products}
          invoiceProducts={invoiceProducts}
          setInvoiceProducts={setInvoiceProducts}
          invoiceSearchQuery={invoiceSearchQuery}
          setInvoiceSearchQuery={setInvoiceSearchQuery}
          expandedProductId={expandedProductId}
          setExpandedProductId={setExpandedProductId}
          onClose={() => {
            setShowInvoiceModal(false);
            setInvoiceProducts([]);
            setInvoiceSearchQuery('');
            setExpandedProductId(null);
          }}
          onCreateInvoice={createInvoice}
        />
      )}

      {/* Invoice View Modal */}
      {selectedInvoiceView && (() => {
        let productDetails = selectedInvoiceView.productDetails || {};
        
        // Ensure productDetails is parsed if it's a string
        if (typeof productDetails === 'string') {
          try {
            productDetails = JSON.parse(productDetails);
          } catch (e) {
            productDetails = {};
          }
        }
        
        const products = productDetails.products || selectedInvoiceView.products || [];
        const date = productDetails.date || selectedInvoiceView.date || new Date(selectedInvoiceView.createdAt).toLocaleDateString('en-IN');
        
        return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: spacing.lg,
          }}
          onClick={() => setSelectedInvoiceView(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              boxShadow: shadows['2xl'],
              padding: spacing.xl,
              width: '100%',
              maxWidth: '960px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            {/* Invoice Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.xl, paddingBottom: spacing.xl, borderBottom: `2px solid ${colors.border}` }}>
              <div>
                <p style={{ fontSize: '1.5rem', fontWeight: 900, color: colors.primary[600], margin: 0, marginBottom: spacing.xs }}>
                  HCS Enterprises
                </p>
                <p style={{ fontSize: '0.85rem', color: colors.text.secondary, margin: 0 }}>
                  12-2-460/1/1,2 Mahek's Vijaya Vihar, Gudi Malkapur Road, Hyderabad, Telangana 500006
                </p>
                <p style={{ fontSize: '0.85rem', color: colors.text.secondary, margin: 0 }}>
                  GSTIN: 36DBAPS4860J2Z9
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: '1.25rem', fontWeight: 900, color: colors.primary[600], margin: 0, marginBottom: spacing.xs }}>
                  {selectedInvoiceView.invoiceNumber}
                </p>
                <p style={{ fontSize: '0.85rem', color: colors.text.secondary, margin: 0 }}>
                  📅 {date}
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                onClick={() => setSelectedInvoiceView(null)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: colors.text.secondary,
                }}
              >
                ✕
              </motion.button>
            </div>

            {/* Invoice Items */}
            <div style={{ marginBottom: spacing.xl }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 900, color: colors.text.primary, marginBottom: spacing.md, margin: 0 }}>
                Items
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                    {['Product (HSN)', 'Size', 'MRP', 'Discount', 'GST', 'Qty', 'Selling Price', 'Total'].map(header => (
                      <th
                        key={header}
                        style={{
                          padding: spacing.md,
                          textAlign: 'left',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          color: colors.text.primary,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products && products.length > 0 ? products.map((item, idx) => {
                    const qty = parseInt(item.qty) || 1;
                    const mrp = parseFloat(item.mrp) || 0;
                    const price = parseFloat(item.price) || 0;
                    const sp = price > 0 ? price : mrp;
                    const total = sp * qty;
                    return (
                      <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={{ padding: spacing.md, fontSize: '0.9rem', color: colors.text.primary, fontWeight: 600 }}>
                          {item.name || 'N/A'}
                          {item.hsn && <span style={{ color: colors.text.secondary, fontWeight: 500 }}> (HSN: {item.hsn})</span>}
                        </td>
                        <td style={{ padding: spacing.md, fontSize: '0.9rem', color: colors.text.secondary }}>
                          {item.sizeName || 'N/A'}
                        </td>
                        <td style={{ padding: spacing.md, fontSize: '0.9rem', color: colors.text.primary, fontWeight: 600 }}>
                          ₹{mrp.toFixed(2)}
                        </td>
                        <td style={{ padding: spacing.md, fontSize: '0.9rem', color: colors.warning }}>
                          {typeof item.discount === 'number' ? item.discount.toFixed(1) : item.discount}%
                        </td>
                        <td style={{ padding: spacing.md, fontSize: '0.9rem', color: colors.text.primary, fontWeight: 600 }}>
                          {item.gst || 5}%
                        </td>
                        <td style={{ padding: spacing.md, fontSize: '0.9rem', color: colors.text.primary, fontWeight: 600 }}>
                          {qty}
                        </td>
                        <td style={{ padding: spacing.md, fontSize: '0.9rem', color: colors.primary[600], fontWeight: 700 }}>
                          ₹{sp.toFixed(2)}
                        </td>
                        <td style={{ padding: spacing.md, fontSize: '0.9rem', color: colors.primary[600], fontWeight: 900 }}>
                          ₹{total.toFixed(2)}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="8" style={{ padding: spacing.lg, textAlign: 'center', color: colors.text.secondary }}>
                        No items in this invoice
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Amount */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: spacing.lg }}>
              <div style={{ width: '250px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: spacing.md, borderTop: `2px solid ${colors.primary[600]}`, borderBottom: `2px solid ${colors.primary[600]}` }}>
                  <span style={{ fontWeight: 900, color: colors.text.primary }}>Total Amount:</span>
                  <span style={{ fontSize: '1.25rem', fontWeight: 900, color: colors.primary[600] }}>
                    ₹{parseFloat(selectedInvoiceView.totalAmount).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ textAlign: 'center', paddingTop: spacing.lg, borderTop: `1px solid ${colors.border}` }}>
              <p style={{ fontSize: '0.8rem', color: colors.text.secondary, margin: 0 }}>
                Thank you for your business!
              </p>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => window.print()}
                style={{
                  marginTop: spacing.md,
                  backgroundColor: colors.primary[600],
                  color: 'white',
                  padding: `${spacing.sm} ${spacing.lg}`,
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                🖨️ Print Invoice
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
        );
      })()}

      {/* Quotation Modal */}
      {isQuotationModalOpen && (
        <QuotationModal
          isOpen={isQuotationModalOpen}
          onClose={() => setIsQuotationModalOpen(false)}
          colors={colors}
          spacing={spacing}
          shadows={shadows}
        />
      )}

      {/* Manual Invoice Modal */}
      {showManualInvoiceModal && (
        <ManualInvoiceModal
          onClose={() => setShowManualInvoiceModal(false)}
          onCreate={createManualInvoice}
        />
      )}
    </div>
  );
}

// Manual Invoice Modal - enter everything manually with HSN code
function ManualInvoiceModal({ onClose, onCreate }) {
  const [items, setItems] = useState([
    { name: '', hsn: '', mrp: '', price: '', discount: '', gst: '5', qty: '1' },
  ]);

  const calculatePriceFromMrpAndDiscount = (mrp, discount) => {
    const parsedMrp = parseFloat(mrp) || 0;
    const parsedDiscount = parseFloat(discount) || 0;
    if (!parsedMrp) return 0;
    return Math.round(parsedMrp - (parsedMrp * parsedDiscount / 100));
  };

  const calculateDiscountFromMrpAndPrice = (mrp, price) => {
    const parsedMrp = parseFloat(mrp) || 0;
    const parsedPrice = parseFloat(price) || 0;
    if (!parsedMrp || parsedPrice >= parsedMrp) return 0;
    return ((parsedMrp - parsedPrice) / parsedMrp) * 100;
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    const current = updated[idx];
    updated[idx] = { ...current, [field]: value };

    if (field === 'mrp') {
      updated[idx].price = calculatePriceFromMrpAndDiscount(value, current.discount);
    } else if (field === 'discount') {
      updated[idx].price = calculatePriceFromMrpAndDiscount(current.mrp, value);
    } else if (field === 'price') {
      const parsedPrice = parseFloat(value) || 0;
      updated[idx].discount = calculateDiscountFromMrpAndPrice(current.mrp, parsedPrice);
      updated[idx].price = parsedPrice;
    }

    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { name: '', hsn: '', mrp: '', price: '', discount: '', gst: '5', qty: '1' }]);
  };

  const removeItem = (idx) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const calcSellingPrice = (mrp, discount, qty, price) => {
    const m = parseFloat(mrp) || 0;
    const d = parseFloat(discount) || 0;
    const q = parseInt(qty) || 1;
    const parsedPrice = parseFloat(price);
    const effectivePrice = Number.isFinite(parsedPrice) && parsedPrice >= 0 ? parsedPrice : (m - (m * d / 100));
    return Math.round(effectivePrice * q);
  };

  const totalAmount = items.reduce((sum, item) => sum + calcSellingPrice(item.mrp, item.discount, item.qty), 0);

  const handleCreate = () => {
    const validItems = items.filter(item => item.name && item.mrp);
    if (validItems.length === 0) {
      alert('❌ Please add at least one item with name and MRP');
      return;
    }
    onCreate(validItems);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: spacing.lg,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows['2xl'],
          padding: spacing.xl,
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: colors.text.primary, margin: 0 }}>
            📄 Manual Invoice
          </h2>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: colors.text.secondary,
              padding: spacing.sm,
            }}
          >
            ✕
          </motion.button>
        </div>

        <p style={{ fontSize: '0.9rem', color: colors.text.secondary, marginBottom: spacing.lg, margin: 0 }}>
          Enter all item details manually. HSN code is included for each item. Enter MRP, price, or discount and the other values update automatically.
        </p>

        {/* Items Table */}
        <div style={{ overflowX: 'auto', marginBottom: spacing.lg }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: colors.primary[100], borderBottom: `2px solid ${colors.border}` }}>
                {['Item Name', 'HSN Code', 'MRP (₹)', 'Price (₹)', 'Disc %', 'GST %', 'Qty', 'Total', ''].map(header => (
                  <th
                    key={header}
                    style={{
                      padding: spacing.sm,
                      textAlign: 'left',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: colors.text.primary,
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const itemTotal = calcSellingPrice(item.mrp, item.discount, item.qty, item.price);
                return (
                  <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: spacing.sm }}>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(idx, 'name', e.target.value)}
                        placeholder="Item name"
                        style={{ width: '140px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', fontSize: '0.85rem' }}
                      />
                    </td>
                    <td style={{ padding: spacing.sm }}>
                      <input
                        type="text"
                        value={item.hsn}
                        onChange={(e) => updateItem(idx, 'hsn', e.target.value)}
                        placeholder="HSN"
                        style={{ width: '80px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', fontSize: '0.85rem' }}
                      />
                    </td>
                    <td style={{ padding: spacing.sm }}>
                      <input
                        type="number"
                        value={item.mrp}
                        onChange={(e) => updateItem(idx, 'mrp', e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        style={{ width: '70px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', fontSize: '0.85rem' }}
                      />
                    </td>
                    <td style={{ padding: spacing.sm }}>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => updateItem(idx, 'price', e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        style={{ width: '70px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', fontSize: '0.85rem' }}
                      />
                    </td>
                    <td style={{ padding: spacing.sm }}>
                      <input
                        type="number"
                        value={item.discount}
                        onChange={(e) => updateItem(idx, 'discount', e.target.value)}
                        placeholder="0"
                        step="0.01"
                        style={{ width: '60px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', fontSize: '0.85rem' }}
                      />
                    </td>
                    <td style={{ padding: spacing.sm }}>
                      <select
                        value={item.gst}
                        onChange={(e) => updateItem(idx, 'gst', e.target.value)}
                        style={{ width: '60px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', fontSize: '0.85rem' }}
                      >
                        <option value="5">5%</option>
                        <option value="18">18%</option>
                      </select>
                    </td>
                    <td style={{ padding: spacing.sm }}>
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => updateItem(idx, 'qty', Math.max(1, parseInt(e.target.value) || 1))}
                        min="1"
                        style={{ width: '50px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', fontSize: '0.85rem' }}
                      />
                    </td>
                    <td style={{ padding: spacing.sm, fontWeight: 700, color: colors.primary[600], fontSize: '0.85rem' }}>
                      ₹{itemTotal}
                    </td>
                    <td style={{ padding: spacing.sm }}>
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => removeItem(idx)}
                        disabled={items.length === 1}
                        style={{
                          backgroundColor: items.length === 1 ? colors.neutral[200] : colors.error,
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          cursor: items.length === 1 ? 'not-allowed' : 'pointer',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                        }}
                      >
                        ✕
                      </motion.button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={addItem}
          style={{
            backgroundColor: colors.primary[100],
            color: colors.primary[700],
            border: 'none',
            borderRadius: '0.3rem',
            padding: `${spacing.sm} ${spacing.md}`,
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: '0.85rem',
            marginBottom: spacing.lg,
          }}
        >
          + Add Item
        </motion.button>

        {/* Total */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: spacing.lg, padding: spacing.md, backgroundColor: colors.primary[50], borderRadius: '0.5rem' }}>
          <span style={{ fontWeight: 900, fontSize: '1.1rem', color: colors.text.primary }}>
            Total Amount: <span style={{ color: colors.primary[600] }}>₹{totalAmount}</span>
          </span>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: spacing.md }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleCreate}
            style={{
              flex: 1,
              backgroundColor: colors.primary[600],
              color: 'white',
              padding: spacing.lg,
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ✅ Create Manual Invoice (₹{totalAmount})
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            style={{
              flex: 1,
              backgroundColor: colors.neutral[200],
              color: colors.text.primary,
              padding: spacing.lg,
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ❌ Cancel
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Inline Create Invoice Modal component with editable fields
function CreateInvoiceModal({ products, invoiceProducts, setInvoiceProducts, invoiceSearchQuery, setInvoiceSearchQuery, expandedProductId, setExpandedProductId, onClose, onCreateInvoice }) {
  const [editableProducts, setEditableProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(true);

  // Initialize editable products when invoice products change
  useEffect(() => {
    setEditableProducts(invoiceProducts.map(p => {
      const mrp = parseFloat(p.price) || 0;
      const discount = parseFloat(p.discount) || 0;
      const calculatedPrice = mrp > 0 ? Math.round(mrp - (mrp * discount / 100)) : 0;

      return {
        ...p,
        editableMrp: mrp,
        editablePrice: calculatedPrice,
        editableDiscount: discount,
        editableGst: parseFloat(p.gst) || 5,
        editableQty: 1,
      };
    }));
  }, [invoiceProducts]);

  // Calculate selling price
  const calcSellingPrice = (mrp, discount) => {
    return Math.round(mrp - (mrp * discount / 100));
  };

  const calculatePriceFromMrpAndDiscount = (mrp, discount) => {
    const parsedMrp = parseFloat(mrp) || 0;
    const parsedDiscount = parseFloat(discount) || 0;
    if (!parsedMrp) return 0;
    return Math.round(parsedMrp - (parsedMrp * parsedDiscount / 100));
  };

  const calculateDiscountFromMrpAndPrice = (mrp, price) => {
    const parsedMrp = parseFloat(mrp) || 0;
    const parsedPrice = parseFloat(price) || 0;
    if (!parsedMrp || parsedPrice >= parsedMrp) return 0;
    return ((parsedMrp - parsedPrice) / parsedMrp) * 100;
  };

  // Update editable field
  const updateProduct = (idx, field, value) => {
    const updated = [...editableProducts];
    const current = updated[idx];
    const nextValue = field === 'editableMrp' || field === 'editableDiscount' || field === 'editablePrice' ? value : parseFloat(value) || 0;

    updated[idx] = { ...current, [field]: nextValue };

    if (field === 'editableMrp') {
      updated[idx].editablePrice = calculatePriceFromMrpAndDiscount(nextValue, current.editableDiscount);
    } else if (field === 'editableDiscount') {
      updated[idx].editablePrice = calculatePriceFromMrpAndDiscount(current.editableMrp, nextValue);
    } else if (field === 'editablePrice') {
      updated[idx].editableDiscount = calculateDiscountFromMrpAndPrice(current.editableMrp, nextValue);
    }

    setEditableProducts(updated);
  };

  // Calculate totals
  const totalAmount = editableProducts.reduce((sum, p) => {
    const sp = parseFloat(p.editablePrice) || calcSellingPrice(p.editableMrp, p.editableDiscount);
    return sum + (sp * p.editableQty);
  }, 0);

  const handleCreateInvoice = () => {
    onCreateInvoice(editableProducts.map(p => {
      const mrp = parseFloat(p.editableMrp) || 0;
      const price = parseFloat(p.editablePrice) || calcSellingPrice(mrp, p.editableDiscount);
      const discount = parseFloat(p.editableDiscount) || 0;

      return {
        ...p,
        mrp,
        price,
        discount,
        gst: p.editableGst,
        qty: p.editableQty,
        sellingPrice: price * p.editableQty,
      };
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: spacing.lg,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows['2xl'],
          padding: spacing.xl,
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: colors.text.primary, margin: 0 }}>
            📄 Create Invoice
          </h2>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: colors.text.secondary,
              padding: spacing.sm,
            }}
          >
            ✕
          </motion.button>
        </div>

        {/* Step 1: Select Products */}
        <div style={{ marginBottom: spacing.lg }}>
          <p style={{ fontSize: '0.9rem', color: colors.text.secondary, marginBottom: spacing.md, margin: 0 }}>
            Enter MRP, price, or discount and the values will stay in sync automatically.
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
              Step 1: Select Products
            </h3>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowProductSelector(!showProductSelector)}
              style={{
                backgroundColor: colors.primary[100],
                color: colors.primary[700],
                border: 'none',
                borderRadius: '0.3rem',
                padding: `${spacing.xs} ${spacing.md}`,
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              {showProductSelector ? 'Hide Products' : 'Show Products'}
            </motion.button>
          </div>

          {showProductSelector && (
            <>
              <input
                type="text"
                placeholder="🔍 Search products..."
                value={invoiceSearchQuery}
                onChange={(e) => setInvoiceSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: `${spacing.md} ${spacing.lg}`,
                  border: `2px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  marginBottom: spacing.lg,
                  outline: 'none',
                }}
              />

              <div style={{ marginBottom: spacing.lg, maxHeight: '200px', overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: '0.5rem' }}>
                {products
                  .filter(p => p.name.toLowerCase().includes(invoiceSearchQuery.toLowerCase()))
                  .map(product => (
                    <div key={product.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <motion.button
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setExpandedProductId(expandedProductId === product.id ? null : product.id)}
                        style={{
                          width: '100%',
                          padding: spacing.lg,
                          backgroundColor: expandedProductId === product.id ? colors.primary[50] : 'white',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <span style={{ fontWeight: 700, color: colors.text.primary, textAlign: 'left' }}>
                          📦 {product.name}
                          <span style={{ display: 'block', fontSize: '0.75rem', color: colors.text.secondary, fontWeight: 500 }}>HSN: {product.hsn || 'N/A'}</span>
                        </span>
                        <span style={{ fontSize: '1.2rem', color: colors.primary[600] }}>
                          {expandedProductId === product.id ? '▼' : '▶'}
                        </span>
                      </motion.button>

                      {expandedProductId === product.id && product.sizes && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          transition={{ duration: 0.2 }}
                          style={{
                            padding: spacing.lg,
                            backgroundColor: colors.primary[25],
                            borderTop: `1px solid ${colors.border}`,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
                            gap: spacing.md,
                          }}
                        >
                          {product.sizes.map(size => (
                            <motion.button
                              key={size.sizeId}
                              whileTap={{ scale: 0.9 }}
                              title={`HSN: ${product.hsn || 'N/A'}`}
                              onClick={() => {
                                const isSelected = invoiceProducts.some(p => p.id === product.id && p.selectedSize === size.sizeId);
                                if (isSelected) {
                                  setInvoiceProducts(invoiceProducts.filter(p => !(p.id === product.id && p.selectedSize === size.sizeId)));
                                } else {
                                  setInvoiceProducts([
                                    ...invoiceProducts,
                                    { ...product, selectedSize: size.sizeId, sizeName: size.name }
                                  ]);
                                }
                              }}
                              style={{
                                padding: spacing.md,
                                borderRadius: '0.4rem',
                                border: `2px solid ${invoiceProducts.some(p => p.id === product.id && p.selectedSize === size.sizeId) ? colors.primary[600] : colors.border}`,
                                backgroundColor: invoiceProducts.some(p => p.id === product.id && p.selectedSize === size.sizeId) ? colors.primary[600] : 'white',
                                fontWeight: 700,
                                cursor: 'pointer',
                                color: invoiceProducts.some(p => p.id === product.id && p.selectedSize === size.sizeId) ? 'white' : colors.text.primary,
                                fontSize: '0.85rem',
                              }}
                              >
                                <div>{size.name}</div>
                                <div style={{ fontSize: '0.7rem', color: colors.text.secondary, marginTop: '0.125rem' }}>HSN: {product.hsn || 'N/A'}</div>
                              </motion.button>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}

          {/* Selected Products List with Editable Fields */}
          {editableProducts.length > 0 && (
            <div style={{ marginBottom: spacing.lg }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0, marginBottom: spacing.md }}>
                ✅ Step 2: Edit Pricing ({editableProducts.length} items)
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: colors.primary[100], borderBottom: `2px solid ${colors.border}` }}>
                      <th style={{ padding: spacing.sm, textAlign: 'left', fontWeight: 700, fontSize: '0.8rem' }}>Product</th>
                      <th style={{ padding: spacing.sm, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>MRP (₹)</th>
                      <th style={{ padding: spacing.sm, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Price (₹)</th>
                      <th style={{ padding: spacing.sm, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Disc %</th>
                      <th style={{ padding: spacing.sm, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>GST %</th>
                      <th style={{ padding: spacing.sm, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Qty</th>
                      <th style={{ padding: spacing.sm, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Total</th>
                      <th style={{ padding: spacing.sm, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editableProducts.map((item, idx) => {
                      const sp = parseFloat(item.editablePrice) || calcSellingPrice(item.editableMrp, item.editableDiscount);
                      const total = sp * item.editableQty;
                      return (
                        <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={{ padding: spacing.sm, fontWeight: 600, fontSize: '0.85rem' }}>
                            {item.name}
                            {item.hsn && <span style={{ color: colors.text.secondary, fontWeight: 500 }}> (HSN: {item.hsn})</span>}
                            {item.sizeName && <div style={{ color: colors.text.secondary, fontWeight: 500, fontSize: '0.8rem' }}>Size: {item.sizeName}</div>}
                          </td>
                          <td style={{ padding: spacing.sm }}>
                            <input
                              type="number"
                              value={item.editableMrp}
                              onChange={(e) => updateProduct(idx, 'editableMrp', e.target.value)}
                              style={{ width: '70px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', textAlign: 'center', fontSize: '0.85rem' }}
                            />
                          </td>
                          <td style={{ padding: spacing.sm }}>
                            <input
                              type="number"
                              value={item.editablePrice}
                              onChange={(e) => updateProduct(idx, 'editablePrice', e.target.value)}
                              style={{ width: '70px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', textAlign: 'center', fontSize: '0.85rem' }}
                            />
                          </td>
                          <td style={{ padding: spacing.sm }}>
                            <input
                              type="number"
                              value={item.editableDiscount}
                              onChange={(e) => updateProduct(idx, 'editableDiscount', e.target.value)}
                              style={{ width: '60px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', textAlign: 'center', fontSize: '0.85rem' }}
                            />
                          </td>
                          <td style={{ padding: spacing.sm }}>
                            <select
                              value={item.editableGst}
                              onChange={(e) => updateProduct(idx, 'editableGst', e.target.value)}
                              style={{ width: '60px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', fontSize: '0.85rem' }}
                            >
                              <option value="5">5%</option>
                              <option value="18">18%</option>
                            </select>
                          </td>
                          <td style={{ padding: spacing.sm }}>
                            <input
                              type="number"
                              value={item.editableQty}
                              min="1"
                              onChange={(e) => updateProduct(idx, 'editableQty', Math.max(1, parseInt(e.target.value) || 1))}
                              style={{ width: '50px', padding: `${spacing.xs} ${spacing.sm}`, border: `1px solid ${colors.border}`, borderRadius: '0.25rem', textAlign: 'center', fontSize: '0.85rem' }}
                            />
                          </td>
                          <td style={{ padding: spacing.sm, fontWeight: 700, color: colors.primary[600], textAlign: 'center', fontSize: '0.85rem' }}>
                            ₹{total}
                          </td>
                          <td style={{ padding: spacing.sm, textAlign: 'center' }}>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                const filtered = editableProducts.filter((_, i) => i !== idx);
                                setEditableProducts(filtered);
                                setInvoiceProducts(invoiceProducts.filter((_, i) => i !== idx));
                              }}
                              style={{
                                backgroundColor: colors.error,
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                padding: `${spacing.xs} ${spacing.sm}`,
                                cursor: 'pointer',
                                fontWeight: 700,
                                fontSize: '0.75rem',
                              }}
                            >
                              ✕
                            </motion.button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Total */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: spacing.md, padding: spacing.md, backgroundColor: colors.primary[50], borderRadius: '0.5rem' }}>
                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: colors.text.primary }}>
                  Total Amount: <span style={{ color: colors.primary[600] }}>₹{totalAmount}</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: spacing.md }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleCreateInvoice}
            disabled={editableProducts.length === 0}
            style={{
              flex: 1,
              backgroundColor: editableProducts.length === 0 ? colors.neutral[300] : colors.primary[600],
              color: 'white',
              padding: spacing.lg,
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              cursor: editableProducts.length === 0 ? 'not-allowed' : 'pointer',
              opacity: editableProducts.length === 0 ? 0.6 : 1,
            }}
          >
            ✅ Create Invoice (₹{totalAmount})
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            style={{
              flex: 1,
              backgroundColor: colors.neutral[200],
              color: colors.text.primary,
              padding: spacing.lg,
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ❌ Cancel
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default AdminDashboard;